import React, { useEffect, useState } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../utils/api";
import {
  Card, CardBody, PageHeader, Badge,
} from "../../components/ui";
import {
  FaEdit, FaTrash, FaPlus, FaTimes, FaCheck, FaCubes,
  FaWhatsapp, FaRobot, FaAddressBook, FaCloud, FaUsers,
  FaToggleOn, FaHashtag, FaSeedling,
} from "react-icons/fa";
import { useModal } from "../../context/ModalContext";

const empty = { key: "", label: "", type: "value", module: "General" };

const MODULE_CONFIG = {
  Messaging: { icon: FaWhatsapp, color: "text-green-500", bg: "bg-green-500/10", border: "border-green-500/20" },
  Automation: { icon: FaRobot, color: "text-indigo-500", bg: "bg-indigo-500/10", border: "border-indigo-500/20" },
  CRM: { icon: FaAddressBook, color: "text-purple-500", bg: "bg-purple-500/10", border: "border-purple-500/20" },
  Storage: { icon: FaCloud, color: "text-cyan-500", bg: "bg-cyan-500/10", border: "border-cyan-500/20" },
  Team: { icon: FaUsers, color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20" },
  General: { icon: FaCubes, color: "text-ink-muted", bg: "bg-surface-bg", border: "border-surface-border" },
};

export default function ManageFeatures() {
  const modal = useModal();
  const [features, setFeatures] = useState([]);
  const [form, setForm] = useState({ ...empty });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = async () => {
    const res = await apiGet("/api/features");
    setFeatures(res?.data || []);
  };

  useEffect(() => { load(); }, []);

  const modules = [...new Set(features.map((f) => f.module || "General"))].sort();

  const submit = async (e) => {
    e.preventDefault();
    let res;
    if (editingId) res = await apiPut(`/api/features/${editingId}`, form);
    else res = await apiPost("/api/features", form);

    if (res?.success) {
      showToast(editingId ? "Feature updated" : "Feature created");
      setForm({ ...empty });
      setEditingId(null);
      setShowForm(false);
      load();
    } else {
      showToast(res?.message || "Failed to save feature", "error");
    }
  };

  const del = async (id) => {
    if (!(await modal.confirm("Delete this feature? It will be removed from plan definitions.", { danger: true, confirmText: "Delete" }))) return;
    const res = await apiDelete(`/api/features/${id}`);
    if (res?.success) { showToast("Feature deleted"); load(); }
    else showToast("Failed to delete", "error");
  };

  const edit = (f) => {
    setEditingId(f.id);
    setForm({ key: f.key, label: f.label, type: f.type, module: f.module || "General" });
    setShowForm(true);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...empty });
    setShowForm(true);
  };

  const seedFeatures = async () => {
    setSeeding(true);
    try {
      const res = await apiPost("/api/admin/features/seed");
      if (res?.success) {
        showToast(`${res.data?.length || 0} features seeded`);
        load();
      } else {
        showToast(res?.message || "Failed to seed", "error");
      }
    } catch {
      showToast("Failed to seed features", "error");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div>
      <PageHeader title="Manage Features" subtitle="Define feature keys and organize them into modules">
        <div className="flex items-center gap-2">
          {features.length === 0 && (
            <button
              className="btn-secondary flex items-center gap-2"
              onClick={seedFeatures}
              disabled={seeding}
            >
              <FaSeedling size={11} />
              {seeding ? "Seeding..." : "Seed Defaults"}
            </button>
          )}
          <button className="btn-primary flex items-center gap-2" onClick={openCreate}>
            <FaPlus size={11} /> New Feature
          </button>
        </div>
      </PageHeader>

      {/* Toast */}
      {toast && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${
          toast.type === "error"
            ? "bg-red-500/10 text-red-500 border border-red-500/20"
            : "bg-green-500/10 text-green-500 border border-green-500/20"
        }`}>
          {toast.type === "error" ? "!" : <FaCheck size={12} />} {toast.msg}
        </div>
      )}

      {/* Stats bar */}
      {features.length > 0 && (
        <div className="flex items-center gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-card border border-surface-border">
            <span className="text-xs text-ink-muted">Total</span>
            <span className="text-sm font-bold text-ink-primary">{features.length}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-card border border-surface-border">
            <FaToggleOn size={12} className="text-brand" />
            <span className="text-xs text-ink-muted">Boolean</span>
            <span className="text-sm font-bold text-ink-primary">{features.filter(f => f.type === "bool").length}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-card border border-surface-border">
            <FaHashtag size={11} className="text-amber-500" />
            <span className="text-xs text-ink-muted">Quota</span>
            <span className="text-sm font-bold text-ink-primary">{features.filter(f => f.type === "value").length}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-card border border-surface-border">
            <FaCubes size={11} className="text-purple-500" />
            <span className="text-xs text-ink-muted">Modules</span>
            <span className="text-sm font-bold text-ink-primary">{modules.length}</span>
          </div>
        </div>
      )}

      {/* Features grouped by module — card grid */}
      {modules.map((mod) => {
        const modFeatures = features.filter((f) => (f.module || "General") === mod);
        const config = MODULE_CONFIG[mod] || MODULE_CONFIG.General;
        const ModIcon = config.icon;

        return (
          <div key={mod} className="mb-6">
            {/* Module header */}
            <div className="flex items-center gap-2.5 mb-3">
              <div className={`w-7 h-7 rounded-lg ${config.bg} flex items-center justify-center`}>
                <ModIcon size={13} className={config.color} />
              </div>
              <h3 className="text-sm font-semibold text-ink-primary">{mod}</h3>
              <Badge variant="neutral">{modFeatures.length}</Badge>
            </div>

            {/* Feature cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {modFeatures.map((f) => (
                <div
                  key={f.id}
                  className={`group relative rounded-xl border ${config.border} bg-surface-card p-4 hover:shadow-md transition-all`}
                >
                  {/* Top row: type badge + actions */}
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant={f.type === "bool" ? "brand" : "warning"}>
                      {f.type === "bool" ? (
                        <span className="flex items-center gap-1"><FaToggleOn size={10} /> Boolean</span>
                      ) : (
                        <span className="flex items-center gap-1"><FaHashtag size={9} /> Quota</span>
                      )}
                    </Badge>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        className="p-1.5 rounded-md text-ink-muted hover:text-ink-primary hover:bg-surface-bg"
                        onClick={() => edit(f)}
                        title="Edit"
                      >
                        <FaEdit size={11} />
                      </button>
                      <button
                        className="p-1.5 rounded-md text-ink-muted hover:text-red-500 hover:bg-red-50"
                        onClick={() => del(f.id)}
                        title="Delete"
                      >
                        <FaTrash size={10} />
                      </button>
                    </div>
                  </div>

                  {/* Label */}
                  <h4 className="text-sm font-medium text-ink-primary mb-1">{f.label}</h4>

                  {/* Key */}
                  <code className="text-2xs font-mono text-ink-muted bg-surface-bg px-2 py-0.5 rounded">
                    {f.key}
                  </code>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {features.length === 0 && (
        <Card>
          <CardBody className="text-center py-16">
            <FaCubes size={32} className="text-ink-faint mx-auto mb-3" />
            <p className="text-sm text-ink-muted mb-1">No features defined yet</p>
            <p className="text-xs text-ink-faint mb-4">
              Features are the building blocks of your pricing plans.
            </p>
            <div className="flex items-center justify-center gap-2">
              <button className="btn-secondary btn-sm flex items-center gap-2" onClick={seedFeatures} disabled={seeding}>
                <FaSeedling size={11} />
                {seeding ? "Seeding..." : "Seed Default Features"}
              </button>
              <button className="btn-primary btn-sm flex items-center gap-2" onClick={openCreate}>
                <FaPlus size={11} /> Create Manually
              </button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setShowForm(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <Card className="shadow-2xl">
              <CardBody>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-base font-semibold text-ink-primary flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-brand/10 flex items-center justify-center">
                      <FaCubes size={13} className="text-brand" />
                    </div>
                    {editingId ? "Edit Feature" : "Create Feature"}
                  </h3>
                  <button className="p-1.5 rounded-md text-ink-muted hover:text-ink-primary hover:bg-surface-bg" onClick={() => setShowForm(false)}>
                    <FaTimes size={14} />
                  </button>
                </div>

                <form onSubmit={submit} className="space-y-4">
                  <div className="input-group">
                    <label className="input-label">Feature Key</label>
                    <input
                      className="input font-mono text-sm"
                      placeholder="e.g. whatsappConnections"
                      value={form.key}
                      onChange={(e) => setForm({ ...form, key: e.target.value })}
                      required
                      disabled={!!editingId}
                    />
                    <p className="input-hint">Unique identifier used in code (cannot change after creation)</p>
                  </div>

                  <div className="input-group">
                    <label className="input-label">Display Label</label>
                    <input
                      className="input"
                      placeholder="e.g. WhatsApp Connections"
                      value={form.label}
                      onChange={(e) => setForm({ ...form, label: e.target.value })}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="input-group">
                      <label className="input-label">Module</label>
                      <input
                        className="input"
                        placeholder="e.g. Messaging"
                        value={form.module}
                        onChange={(e) => setForm({ ...form, module: e.target.value })}
                        list="module-suggestions"
                      />
                      <datalist id="module-suggestions">
                        {modules.map((m) => <option key={m} value={m} />)}
                      </datalist>
                    </div>

                    <div className="input-group">
                      <label className="input-label">Type</label>
                      <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                        <option value="value">Quota (numeric)</option>
                        <option value="bool">Boolean (on/off)</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2">
                      <FaCheck size={11} />
                      {editingId ? "Update Feature" : "Create Feature"}
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                  </div>
                </form>
              </CardBody>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
